/**
 *
 * RangeSlider
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const thumbSize = '16px';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 ${thumbSize};
  padding-top: 1.6rem;
  height: calc(${thumbSize} + 1.6rem);

  .input-wrapper {
    width: calc(100% + ${thumbSize});
    margin: 0 calc(${thumbSize} / -2);
    position: absolute;
    height: ${thumbSize};
  }

  .control-wrapper {
    width: 100%;
    position: absolute;
    height: ${thumbSize};
  }

  .rail {
    position: absolute;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);
    height: 6px;
    border-radius: 3px;
    background: lightgrey;
  }

  .inner-rail {
    position: absolute;
    height: 100%;
    opacity: 0.5;
  }
`;

const Input = styled.input`
  position: absolute;
  width: 100%;
  pointer-events: none;
  appearance: none;
  height: 100%;
  opacity: 0;
  z-index: ${(props) => (props.isAtFinish ? 4 : 3)};
  padding: 0;

  &::-ms-track {
    appearance: none;
    background: transparent;
    border: transparent;
  }
  &::-moz-range-track {
    appearance: none;
    background: transparent;
    border: transparent;
  }
  &:focus::-webkit-slider-runnable-track {
    appearance: none;
    background: transparent;
    border: transparent;
  }

  &::-ms-thumb {
    appearance: none;
    pointer-events: all;
    width: ${thumbSize};
    height: ${thumbSize};
    border-radius: 0px;
    border: 0 none;
    cursor: grab;
    background-color: red;
    transform: translateX(${(props) => (props.isOverlapping ? `${props.isMin ? '-' : ''}50%` : '0')});

    &:active {
      cursor: grabbing;
    }
  }
  &::-moz-range-thumb {
    appearance: none;
    pointer-events: all;
    width: ${thumbSize};
    height: ${thumbSize};
    border-radius: 0px;
    border: 0 none;
    cursor: grab;
    background-color: red;
    transform: translateX(${(props) => (props.isOverlapping ? `${props.isMin ? '-' : ''}50%` : '0')});

    &:active {
      cursor: grabbing;
    }
  }
  &::-webkit-slider-thumb {
    appearance: none;
    pointer-events: all;
    width: ${thumbSize};
    height: ${thumbSize};
    border-radius: 0px;
    border: 0 none;
    cursor: grab;
    background-color: red;
    transform: translateX(${(props) => (props.isOverlapping ? `${props.isMin ? '-' : ''}50%` : '0')});

    &:active {
      cursor: grabbing;
    }
  }
`;

const Control = styled.div`
  width: ${thumbSize};
  height: ${thumbSize};
  border-radius: 50%;
  position: absolute;
  background: ${(props) =>
    props.isOverlapping
      ? `linear-gradient(to ${props.isMin ? 'right' : 'left'}, ${props.color} 40%, transparent 40%)`
      : props.color};
  top: 50%;
  left: ${(props) => props.left}%;
  margin-left: calc(${thumbSize} / -2);
  transform: translate3d(0, -50%, 0);
  z-index: 2;

  &::before {
    content: '${(props) => props.label}';
    position: absolute;
    bottom: calc(${thumbSize} + 0.25rem);
    left: calc(${thumbSize} / 2);
    transform: translateX(-50%);
  }
`;

class RangeSlider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      minValue: props.value ? JSON.parse(props.value).min : props.min,
      maxValue: props.value ? JSON.parse(props.value).max : props.max,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value && this.props.value !== nextProps.value) {
      this.setState({ minValue: JSON.parse(nextProps.value).min, maxValue: JSON.parse(nextProps.value).max });
    }
  }

  handleMinChange = (e) => {
    e.preventDefault();
    const newMinVal = Math.min(+e.target.value, this.state.maxValue);
    if (!this.props.value) {
      this.setState({ minValue: newMinVal });
    }
    this.props.onChange({ min: newMinVal, max: this.state.maxValue });
  };

  handleMaxChange = (e) => {
    e.preventDefault();
    const newMaxVal = Math.max(+e.target.value, this.state.minValue);
    if (!this.props.value) {
      this.setState({ maxValue: newMaxVal });
    }
    this.props.onChange({ min: this.state.minValue, max: newMaxVal });
  };

  render() {
    const { min, max, step, stepLabels, color } = this.props;
    const { minValue, maxValue } = this.state;

    const minPos = ((minValue - min) / (max - min)) * 100;
    const maxPos = ((maxValue - min) / (max - min)) * 100;

    return (
      <Wrapper>
        <div className="input-wrapper">
          <Input
            isMin
            type="range"
            value={minValue}
            min={min}
            max={max}
            step={step}
            isOverlapping={minValue === maxValue}
            onChange={this.handleMinChange}
          />
          <Input
            isMax
            type="range"
            value={maxValue}
            min={min}
            max={max}
            step={step}
            isOverlapping={minValue === maxValue}
            onChange={this.handleMaxChange}
          />
        </div>

        <div className="control-wrapper">
          <Control
            isMin
            color={color}
            left={minPos}
            label={stepLabels[minValue]}
            isOverlapping={minValue === maxValue}
          />
          <div className="rail">
            <div className="inner-rail" style={{ background: color, left: `${minPos}%`, right: `${100 - maxPos}%` }} />
          </div>
          <Control
            isMax
            color={color}
            left={maxPos}
            label={stepLabels[maxValue]}
            isOverlapping={minValue === maxValue}
          />
        </div>
      </Wrapper>
    );
  }
}

RangeSlider.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.string,
  step: PropTypes.number,
  stepLabels: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  color: PropTypes.string,
  onChange: PropTypes.func,
};
RangeSlider.defaultProps = {
  color: 'hotpink',
};

export default RangeSlider;



// WEBPACK FOOTER //
// ./app/components/RangeSlider/index.js